import { useState, useEffect } from 'react';
import { socieApi } from '../services/api';
import '../styles/Modal.css';

function GenerateSOCIEModal({ entity, onClose, onComplete }) {
  const [currentStartDate, setCurrentStartDate] = useState('');
  const [currentEndDate, setCurrentEndDate] = useState('');
  const [previousStartDate, setPreviousStartDate] = useState('');
  const [previousEndDate, setPreviousEndDate] = useState('');
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  // Set default fiscal year dates (India: 1 April to 31 March)
  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed

    // If we're before April, use previous year as current FY
    const currentFYYear = currentMonth < 3 ? currentYear - 1 : currentYear;
    const previousFYYear = currentFYYear - 1;

    // Current FY
    setCurrentStartDate(`${currentFYYear}-04-01`);
    setCurrentEndDate(`${currentFYYear + 1}-03-31`);

    // Previous FY
    setPreviousStartDate(`${previousFYYear}-04-01`);
    setPreviousEndDate(`${previousFYYear + 1}-03-31`);
  }, []);

  const handleGenerate = async () => {
    // Validation - only current period is required
    if (!currentStartDate || !currentEndDate) {
      setError('Please fill in current period dates');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setResult(null);

      const response = await socieApi.generateSOCIE(
        entity.name,
        currentStartDate,
        currentEndDate,
        previousStartDate || currentStartDate,  // Use current dates as fallback if previous not provided
        previousEndDate || currentEndDate
      );

      setResult(response);

      // Auto-close and trigger refresh after 3 seconds
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 3000);
    } catch (err) {
      setError('Failed to generate SOCIE: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate SOCIE Report</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {generating && (
            <div className="alert alert-info" style={{ fontSize: '14px' }}>
              SOCIE generation is in progress. This may take up to 5 minutes.
            </div>
          )}
          {result && (
            <div className="alert alert-success">
              <p><strong>SOCIE Generated Successfully!</strong></p>
              <p>Period: {result.period}</p>
              {result.summary?.opening_shares && (
                <p>Opening Shares: {parseFloat(result.summary.opening_shares).toLocaleString('en-IN')}</p>
              )}
              {result.summary?.closing_shares && (
                <p>Closing Shares: {parseFloat(result.summary.closing_shares).toLocaleString('en-IN')}</p>
              )}
              {result.summary?.total_opening_equity && (
                <p>Total Opening Equity: ₹{parseFloat(result.summary.total_opening_equity).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
              )}
              {result.summary?.total_closing_equity && (
                <p>Total Closing Equity: ₹{parseFloat(result.summary.total_closing_equity).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Entity:</label>
            <input
              type="text"
              value={entity.name}
              disabled
              className="form-control"
            />
          </div>

          <div className="form-section">
            <h3>Current Year Period *</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={currentStartDate}
                  onChange={(e) => setCurrentStartDate(e.target.value)}
                  className="form-control"
                  disabled={generating}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={currentEndDate}
                  onChange={(e) => setCurrentEndDate(e.target.value)}
                  className="form-control"
                  disabled={generating}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Previous Year Period (Optional)</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={previousStartDate}
                  onChange={(e) => setPreviousStartDate(e.target.value)}
                  className="form-control"
                  disabled={generating}
                />
              </div>
              <div className="form-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={previousEndDate}
                  onChange={(e) => setPreviousEndDate(e.target.value)}
                  className="form-control"
                  disabled={generating}
                />
              </div>
            </div>
            <small className="form-text">
              Leave empty if you only need current year SOCIE
            </small>
          </div>

          <div className="info-box">
            <p><strong>Note:</strong> The system will:</p>
            <ul>
              <li>Use the uploaded input data (equity share capital and other equity)</li>
              <li>Generate SOCIE Statement with 100% Excel formulas</li>
              <li>All calculations reference the Input Data sheet</li>
              <li>Output is fully transparent and auditable</li>
              <li>Generate Schedule III compliant SOCIE</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={generating}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate SOCIE'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GenerateSOCIEModal;
